import { useTranslation } from "react-i18next";
import { PMDialog } from "../../utils/PMDialog";
import { LaborSettlementDetails } from "./LaborSettlementDetails";
import { CalculationInfoIcon } from "./CalculationInfoIcon";
import { SUMMARY_CALC_GUIDE_KEYS } from "./AccountingSummaryCalcGuides";

const CostLine = ({ label, amount, formatCurrency, hint, onInfoClick, infoAriaLabel }) => (
  <div className="flex items-start justify-between gap-4 text-sm">
    <div className="min-w-0">
      <div className="flex items-center gap-1.5">
        <span className="text-slate-700">{label}</span>
        {onInfoClick && (
          <CalculationInfoIcon
            ariaLabel={infoAriaLabel}
            onClick={onInfoClick}
          />
        )}
      </div>
      {hint && <p className="mt-0.5 text-xs text-slate-500">{hint}</p>}
    </div>
    <span className="shrink-0 font-medium tabular-nums text-slate-900">
      {formatCurrency(amount)}
    </span>
  </div>
);

export const CostsDetailDialog = ({
  open,
  onClose,
  costs,
  formatCurrency,
  year,
  onOpenCalcGuide,
}) => {
  const { t } = useTranslation();

  if (!costs) return null;

  const laborData = costs.labor;
  const laborCosts = parseFloat(laborData?.total ?? laborData ?? 0);
  const supplierInvoiceCosts = parseFloat(costs.supplier_invoices || 0);
  const itemListCosts = parseFloat(costs.item_lists || 0);
  const voucherExpenseCosts = parseFloat(costs.voucher_expenses || 0);
  const totalCosts = parseFloat(costs.total || 0);

  const guide = (key) =>
    onOpenCalcGuide
      ? {
          onInfoClick: () => onOpenCalcGuide(key),
          infoAriaLabel: t(SUMMARY_CALC_GUIDE_KEYS[key].ariaKey),
        }
      : {};

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title="accounting.summary.costs_detail_title"
      size="2xl"
      closeOnOutsideClick
    >
      <div className="space-y-6">
        <p className="text-sm text-slate-600">
          {t("accounting.summary.costs_detail_intro", { year })}
        </p>

        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-3">
          <div className="flex items-center justify-between border-b border-slate-200 pb-3">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-semibold text-slate-900">
                {t("accounting.summary.costs")}
              </span>
              {onOpenCalcGuide && (
                <CalculationInfoIcon
                  ariaLabel={t(SUMMARY_CALC_GUIDE_KEYS.costs.ariaKey)}
                  onClick={() => onOpenCalcGuide("costs")}
                />
              )}
            </div>
            <span className="text-lg font-bold tabular-nums text-slate-900">
              {formatCurrency(totalCosts)}
            </span>
          </div>

          <CostLine
            label={t("accounting.summary.labor")}
            amount={laborCosts}
            formatCurrency={formatCurrency}
            {...guide("labor")}
          />

          {laborData && (
            <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
              <LaborSettlementDetails
                labor={laborData}
                t={t}
                formatCurrency={formatCurrency}
                translationPrefix="accounting.summary"
              />
            </div>
          )}

          <CostLine
            label={t("accounting.summary.supplier_invoices")}
            amount={supplierInvoiceCosts}
            formatCurrency={formatCurrency}
            {...guide("supplier_invoices")}
          />
          <CostLine
            label={t("accounting.summary.voucher_expenses")}
            amount={voucherExpenseCosts}
            formatCurrency={formatCurrency}
            {...guide("voucher_expenses")}
          />
          <CostLine
            label={t("accounting.summary.item_lists")}
            amount={itemListCosts}
            formatCurrency={formatCurrency}
            hint={t("accounting.summary.item_lists_excluded_hint")}
            {...guide("item_lists")}
          />
        </div>

        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-slate-900">
            {t("accounting.summary.calculation_method_title")}
          </h4>
          <p className="text-sm text-slate-600">
            {t("accounting.summary.labor_voucher_explanation")}
          </p>
          <ul className="space-y-2 text-sm text-slate-600">
            <li>
              <span className="font-medium text-slate-800">
                {t("accounting.summary.labor")}:
              </span>{" "}
              {laborData?.source === "voucher"
                ? t("accounting.summary.labor_methodology_voucher")
                : t("accounting.summary.labor_methodology_hours")}
            </li>
            <li>
              <span className="font-medium text-slate-800">
                {t("accounting.summary.supplier_invoices")}:
              </span>{" "}
              {t("accounting.summary.supplier_invoices_methodology")}
            </li>
            <li>
              <span className="font-medium text-slate-800">
                {t("accounting.summary.item_lists")}:
              </span>{" "}
              {t("accounting.summary.item_lists_methodology")}{" "}
              {t("accounting.summary.item_lists_excluded_hint")}
            </li>
            <li>
              <span className="font-medium text-slate-800">
                {t("accounting.summary.voucher_expenses")}:
              </span>{" "}
              {t("accounting.summary.voucher_expenses_methodology")}
            </li>
          </ul>
        </div>
      </div>
    </PMDialog>
  );
};
